import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  HttpError,
  useCreate,
  useOne,
  useParse,
  useTranslation,
} from "@refinedev/core";
import {
  CreateEventStep,
  IAttendantTemplateForm,
  IAttendantTemplateItem,
  ISimpleEventDetail,
} from "interfaces";
import { Link } from "react-router-dom";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import { TemplatesForm } from "./templatesForm";
import toast from "react-hot-toast";
import { AttendantTemplateSchema } from "./validationSchemas";
import { endpointService } from "components/shared/services/endpointService";

interface ParticipantTemplatesStepProps {
  eventDetailData: ISimpleEventDetail;
  refetchEventDetail: () => void;
}

interface ParticipantTemplatesStepFormProps
  extends ParticipantTemplatesStepProps {
  detail: IAttendantTemplateForm;
  refetchTemplates: () => void;
}

export const ParticipantTemplatesStep = (
  props: ParticipantTemplatesStepProps
) => {
  const parse = useParse();
  const { params } = parse();

  const {
    data,
    isLoading: isLoading,
    refetch: refetchTemplates,
  } = useOne<IAttendantTemplateForm>({
    resource: endpointService.getEventEndpoint("getTemplates", {
      eventId: params?.id,
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const attendantTemplateDetail = data?.data ?? null;
  if (isLoading) {
    return <FullScreenLoading />;
  }
  return attendantTemplateDetail ? (
    <ParticipantTemplatesStepForm
      detail={attendantTemplateDetail}
      refetchTemplates={refetchTemplates}
      {...props}
    />
  ) : (
    <></>
  );
};

export const ParticipantTemplatesStepForm = (
  props: ParticipantTemplatesStepFormProps
) => {
  const parse = useParse();
  const { params } = parse();
  const { mutate, isLoading: isLoading } = useCreate();
  const { translate } = useTranslation();

  const { detail, refetchTemplates, refetchEventDetail, eventDetailData } =
    props;

  const methods = useForm<
    IAttendantTemplateForm,
    HttpError,
    IAttendantTemplateForm
  >({
    defaultValues: {
      languages: eventDetailData.languages ?? [],
      templates: detail.templates ?? [],
      event_id: eventDetailData.id ?? "",
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        AttendantTemplateSchema(values.languages)
      );
      return createResolver(values, context, options);
    },
  });

  const _createTemplate = (
    data: IAttendantTemplateItem[],
    callbackMessage?: string
  ) => {
    mutate(
      {
        resource: endpointService.getEventEndpoint("createTemplate"),
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          languages: eventDetailData.languages ?? [],
          event_id: eventDetailData.id ?? "",
          templates: data,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            callbackMessage ??
              translate("toastMessage.addedTemplateSuccessfully")
          );
          refetchTemplates();
          refetchEventDetail();
        },
      }
    );
  };

  console.log(methods.formState.errors);
  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-full gap-y-2 sm:gap-y-4">
        <TemplatesForm _createTemplate={_createTemplate} {...props} />
        <div
          className="w-full mt-auto px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
          style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
        >
          <div className="flex items-center justify-end gap-x-4">
            <Link
              to={`/events/sports/${params?.id}/edit?step=${CreateEventStep.BUILD_EVENT_PAGE}`}
            >
              <Button className="min-w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                {translate("button.back")}
              </Button>
            </Link>
            <Link
              to={`/events/sports/${params?.id}/edit?step=${CreateEventStep.CREATE_TICKET_PHASE}`}
            >
              <Button
                className="min-w-20 disabled:opacity-70"
                disabled={eventDetailData.current_step < params?.step}
              >
                {translate("button.next")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
      {isLoading ? <FullScreenLoading /> : <></>}
    </FormProvider>
  );
};
